import React, { useMemo } from 'react';
import { getLocale, shallowEqual, useLocation, useSelector } from 'umi';
import { parseUrlInfo } from '@/pages/dashboard/utils/utils';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import CopilotIcon from '@/pages/dashboard/assets/svg/copilot-icon.svg';
import './index.scss';

interface IAIAnalysisConfig {
  btnEn?: string;
  btnCn?: string;
  prefixTagEn?: string;
  prefixTagCn?: string;
  queryEn?: string;
  queryCn?: string;
  form?: string;
  buttonId?: string;
}

interface IAIAnalysisButtonProps {
  componentKey?: string;
  pageId: string;
  agentId?: string;
}

const matchAgentConfig = (
  agentInfoConfig: Record<string, any>,
  realPath: string,
  componentKey: string,
): IAIAnalysisConfig | null => {
  if (!agentInfoConfig || !realPath || !componentKey) return null;
  const segments = realPath.split('/');
  for (let i = segments.length;i > 0;i--) {
    const tryPath = segments.slice(0, i).join('/');
    const config = agentInfoConfig[tryPath]?.[componentKey];
    if (config) return config;
  }
  return null;
};

const AIAnalysisButton: React.FC<IAIAnalysisButtonProps> = ({
  componentKey = '',
  pageId,
  agentId = "04f17af1-4477-4229-9f64-099e2a996a1e",
}) => {
  const location = useLocation();
  const isEN = getLocale() === 'en-US';

  const realPath = useMemo(
    () => parseUrlInfo(location.pathname).realPath,
    [location.pathname],
  );

  const { agentInfoConfig, currentGame } = useSelector(
    (state: any) => ({
      agentInfoConfig: state.dashboard?.agentInfoConfig,
      currentGame: state.dashboard?.currentGame,
    }),
    shallowEqual,
  );

  const gameCode: string = currentGame?.game_code || '';
  const gameName: string = isEN
    ? currentGame?.game_name
    : currentGame?.game_cn_name;

  const config = matchAgentConfig(agentInfoConfig, realPath, componentKey);

  const { openCopilot, openCopilotWithAgentId, isHaveAIPermission } = useCopilotOpenBridge();

  if (!config || !isHaveAIPermission || !componentKey) return null;

  const btnText = isEN ? (config.btnEn || 'AI Analysis') : (config.btnCn || 'AI 分析');

  const handleClick = (): void => {
    buriedBtnPoint({
      buttonId: "208501901",
      buttonName: 'AIAnalysis',
      typeId: 'dashboard_pc_v2',
      pageId,
      uidType: 'game_code',
      uid: gameCode,
      gameName: gameName || '',
      extInfo: JSON.stringify({ componentKey, realPath }),
    });

    const prefixTag =
      (isEN ? config.prefixTagEn : config.prefixTagCn) || gameName || '';
    const query = (isEN ? config.queryEn : config.queryCn) || '';
    openCopilotWithAgentId([agentId]);
    openCopilot({ prefixTag, query, form: config.form });
  };

  return (
    <div className="dbd-ai-analysis-btn" onClick={handleClick}>
      <img className="dbd-ai-analysis-btn__icon" src={CopilotIcon} alt="" />
      <span className="dbd-ai-analysis-btn__text">{btnText}</span>
    </div>
  );
};

export default AIAnalysisButton;
